import React, {useEffect} from "react";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {useNavigate} from "react-router-dom";
import {AppDispatch, RootState} from "../../redux/store";
import {useDispatch, useSelector} from "react-redux";
import {logout} from "../../redux/slice/accountSlice";
import {getTransactions, reset} from "../../redux/slice/transactionSlice";
import {ArrowLeftRight} from "lucide-react";
import {DataTable} from "../../components/table/DataTable";
import {HeroHeader, Card, COLORS, formatNaira} from "../../designSystem";

function Transactions() {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {transactions, pageInfo, error, loading} = useSelector((state: RootState) => state.transaction);
    const {token, isAuthenticated} = useSelector((state: RootState) => state.account);

    const [searchText, setSearchText] = React.useState("");
    const [groupBy, setGroupBy] = React.useState<string | undefined>();

    const columns = [
        {
            title: "#",
            key: "index",
            render: (value: any, item: any, index: any) => (pageInfo.pageNumber - 1) * 10 + index + 1,
        },
        {
            title: "Transaction Reference",
            dataIndex: "transactionReference",
            key: "transactionReference",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (text: string) => <span className="font-mono">{`₦${parseFloat(text).toLocaleString()}`}</span>,
        },
        {
            title: "Transaction Amount",
            dataIndex: "transactionAmount",
            key: "transactionAmount",
            render: (text: string) => {
                if (text === null) {
                    return ``
                }
                return <span className="font-mono">{`₦${parseFloat(text).toLocaleString()}`}</span>
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Transaction Action Type",
            dataIndex: "transactionActionType",
            key: "transactionActionType",
        },
        {
            title: "Transaction Type",
            dataIndex: "transactionType",
            key: "transactionType",
        },
        {
            title: "Transaction Status",
            dataIndex: "transactionStatus",
            key: "transactionStatus",
        },
        {
            title: "Initiated At",
            dataIndex: "initiatedAt",
            key: "initiatedAt",
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: "Completed At",
            dataIndex: "completedAt",
            key: "completedAt",
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: "Account Name",
            dataIndex: "accountName",
            key: "accountName",
        },
        {
            title: "Account Number",
            dataIndex: "accountNumber",
            key: "accountNumber",
        },
        {
            title: "Bank Name",
            dataIndex: "bankName",
            key: "bankName",
        },
        {
            title: "Account Status",
            dataIndex: "accountStatus",
            key: "accountStatus",
        },
        {
            title: "Account Created At",
            dataIndex: "accountCreatedAt",
            key: "accountCreatedAt",
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: "Account Expires At",
            dataIndex: "accountExpiresAt",
            key: "accountExpiresAt",
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: "Account Reference",
            dataIndex: "accountReference",
            key: "accountReference",
        },
        {
            title: "Group Savings",
            dataIndex: "groupSavings",
            key: "groupSavings",
        },
        {
            title: "Sub Account",
            dataIndex: "subAccount",
            key: "subAccount",
        },
    ];

    useEffect(() => {
        if (token)
            dispatch(getTransactions({
                token,
                searchParam: searchText,
                pageNumber: pageInfo?.pageNumber || 1
            })).then((res) => {
            }).catch((err) => console.log(err));

        return () => {
            // cleanup
        }
    }, [token, searchText]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (error === 'Unauthorized') {
            dispatch(reset());
            dispatch(logout());
            navigate("/login");
        }
    }, [error]);

    const handlePageChange = (pageNumber: number) => {
        dispatch(getTransactions({
            token,
            searchParam: searchText,
            pageNumber
        })).then((res) => {
        }).catch((err) => console.log(err));
    }

    return (
        <AdminLayout>
            <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
                <HeroHeader
                    icon={ArrowLeftRight}
                    title="Transactions"
                    subtitle="Search and review every transaction processed across the platform."
                />

                <Card className="mt-8 p-5">
                    {/*
                      No date or status control: /payment/transactions accepts only searchParam
                      and paging, so either would filter nothing but the rows already fetched
                      while looking like it filtered the whole result set.
                    */}
                    <DataTable
                        columns={columns}
                        data={transactions}
                        pageInfo={pageInfo}
                        handlePageChange={handlePageChange}
                        loading={loading}
                        searchValue={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search by reference, name or account"
                        grouping={{
                            value: groupBy,
                            onChange: setGroupBy,
                            options: [
                                {key: "transactionStatus", label: "Status", get: (row) => row.transactionStatus},
                                {key: "transactionType", label: "Type", get: (row) => row.transactionType},
                                {key: "transactionActionType", label: "Action", get: (row) => row.transactionActionType},
                                {key: "bankName", label: "Bank", get: (row) => row.bankName},
                                {key: "day", label: "Day initiated", get: (row) => row.initiatedAt ? new Date(row.initiatedAt).toLocaleDateString() : ""},
                            ],
                        }}
                        totals={[{label: "Amount on this page", dataIndex: "amount", format: formatNaira}]}
                        exportFileName="transactions"
                        emptyText="No transactions match these filters."
                    />
                </Card>
            </main>
        </AdminLayout>
    );
}

export default Transactions;
